/**
 * OfferStrip - landing page offer block for the data cleanup pre-release offer.
 *
 * v4.38 - 2026-10-29 - EXPO-QUALIFIER-01 offer strip (Branko 2026-10-29: "Yes Do it"). Under the site header, never
 *         above it. Three rows: the title ("<month> offer: data cleaning"), the list of what we do moving right to
 *         left, two lines (pre-release offer for qualified companies; the tier prices), then the button to /cleanup.
 *         Prices, currency and the offer end date come from GET /api/qualifier/offer-summary (server file
 *         cleanup_qualifier_offer.json); the month in the title is the end date's month. All words come from the
 *         cleanup translation file. The strip is not rendered while loading, after the offer ends, or when the
 *         server cannot answer. The moving list pauses on hover and stays still for visitors who ask for reduced
 *         motion.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { portalRequestUngated } from '../services/portalRequest';
import { money } from '../pages/Cleanup';

export interface OfferSummary {
  offer_open: boolean;
  offer_ends_on: string;
  currency: string;
  tiers: Record<string, number>;
}

const run = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

// The end date is a calendar date (YYYY-MM-DD); its month is read without a time zone shift.
export const offerMonth = (endsOn: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(new Date(`${endsOn}T00:00:00Z`));

const OfferStrip: React.FC = () => {
  const { t, i18n } = useTranslation('cleanup');
  const navigate = useNavigate();
  const [offer, setOffer] = useState<OfferSummary | null>(null);

  useEffect(() => {
    let alive = true;
    portalRequestUngated('/api/qualifier/offer-summary')
      .then(async (res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        const body = (await res.json()) as OfferSummary;
        if (alive) setOffer(body);
      })
      .catch(() => {
        if (alive) setOffer(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!offer || !offer.offer_open || !offer.tiers || Object.keys(offer.tiers).length === 0) return null;

  const items = t('strip.items', { returnObjects: true }) as string[];
  const tierLabels = t('strip.tierLabels', { returnObjects: true }) as Record<string, string>;
  const prices = Object.entries(offer.tiers).map(([tier, cents]) => (
    <Box component="span" key={tier} sx={{ mx: 1, whiteSpace: 'nowrap' }}>
      {tierLabels[tier] || tier}{' '}
      <Box component="span" sx={{ color: 'accent.main' }}>{money(cents, offer.currency)}</Box>
    </Box>
  ));
  const list = (hidden: boolean) =>
    (Array.isArray(items) ? items : []).map((item, i) => (
      <Box
        component="span"
        key={`${hidden ? 'b' : 'a'}${i}`}
        aria-hidden={hidden || undefined}
        sx={{
          px: 2.5,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: -3,
            top: '50%',
            width: 6,
            height: 6,
            mt: '-3px',
            borderRadius: '50%',
            bgcolor: 'accent.main',
          },
        }}
      >
        {item}
      </Box>
    ));

  return (
    <Box component="section" aria-label={t('strip.aria')} data-testid="offer-strip" sx={{ bgcolor: 'secondary.main', color: '#fff', textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, fontWeight: 700, pt: 1.75, pb: 1.25, px: 2 }}>
        {t('strip.title', { month: offerMonth(offer.offer_ends_on, i18n.language || 'en') })}{' '}
        <Box component="span" sx={{ color: 'accent.main' }}>{t('strip.titleEm')}</Box>
      </Typography>
      <Box
        sx={{
          overflow: 'hidden',
          height: 40,
          display: 'flex',
          alignItems: 'center',
          borderTop: '1px solid rgba(255,255,255,0.15)',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
          bgcolor: 'rgba(0,0,0,0.18)',
          '&:hover .offer-track': { animationPlayState: 'paused' },
        }}
      >
        <Box
          className="offer-track"
          data-testid="offer-track"
          sx={{
            display: 'inline-flex',
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.85rem', md: '0.95rem' },
            animation: `${run} 40s linear infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          {list(false)}
          {list(true)}
        </Box>
      </Box>
      <Box sx={{ pt: 1.5, px: 2, lineHeight: 1.5 }}>
        <Typography sx={{ fontSize: { xs: '0.9rem', md: '0.95rem' }, opacity: 0.85 }}>{t('strip.line1')}</Typography>
        <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 700 }}>{prices}</Typography>
      </Box>
      <Box sx={{ pt: 1.25, pb: 2 }}>
        <Button
          variant="contained"
          onClick={() => navigate('/cleanup')}
          sx={{ bgcolor: 'accent.main', color: 'accent.contrastText', fontWeight: 700, px: 3, '&:hover': { bgcolor: 'accent.dark' } }}
        >
          {t('strip.button')}
        </Button>
      </Box>
    </Box>
  );
};

export default OfferStrip;
